import { useEffect, useRef } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { PROMPT_COPY } from './promptCopy'
import styles from './DesktopPromptFrame.module.css'

/**
 * The desktop prompt's frame: the dimmed page, the centred card and its ×
 * (Figma "Close dialog"). A computer cannot install the app, so the card has
 * no store button — the QR code inside it is the way to the phone. × (or
 * Escape, or a click on the dimmed page) closes it; focus starts on the
 * dialog itself, so no button wears a focus ring the visitor did not ask for.
 */
export function DesktopPromptFrame({
  labelledBy,
  variant,
  onClose,
  children,
}: {
  readonly labelledBy: string
  readonly variant: 'v1' | 'v2'
  readonly onClose: () => void
  readonly children: ReactNode
}): ReactElement {
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dialogRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className={styles.overlay}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={`${styles.card} ${styles[variant]}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
      >
        {children}
        <button
          type="button"
          className={styles.close}
          aria-label={PROMPT_COPY.close}
          onClick={onClose}
        >
          ×
        </button>
      </div>
    </div>
  )
}
